'use client';

import {
  Button,
  ButtonGroup,
  Flex,
  Heading,
  EmptyState,
  Select,
  CardTable,
  CardPagination,
} from '@/_components';
import { useMemo, useState, useContext, useCallback, type FC } from 'react';
import {
  ColumnDef,
  getSortedRowModel,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useTable,
  SortingState,
  PaginationState,
  FilterFn,
} from '@tanstack/react-table';
import { PAGINATION_OPTIONS } from '@/_constants/tables';
import { SIGNATURE_REQUEST_TABLE_HEADER_MAP } from '@/_constants/signature-request';
import { rankItem } from '@tanstack/match-sorter-utils';
import { SignatureRequestsContext } from '@/_contexts/SignatureRequestsProvider';
import { SignatureRequestDialog } from './_components/SignatureRequestDialog';
import { SignatureRequestCell } from './_components/SignatureRequestCell';
import {
  updateSignatureRequest,
  deleteSignatureRequest,
} from '@/_api/admin-signature-requests';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import type { SignatureRequest } from '@/_types/models/signatureRequests';
import type { SignatureRequestsTableProps } from './SignatureRequestsTable.types';
import type { SignatureRequestColumnType } from '@/_types/signatureRequest';
import { getClasses } from '@/_utils/classnames';

const SORTING_ENABLED_COLUMNS: Array<SignatureRequestColumnType> = [
  'company_name',
  'tax_year',
  'document_name',
  'status',
  'updated_at',
];

export const SignatureRequestsTable: FC<SignatureRequestsTableProps> = ({
  isInline,
  refreshCompanySignatureRequests,
  search,
  signatureRequestsAlreadyLoaded,
}) => {
  const {
    signatureRequests: signatureRequestsFromContext,
    getSignatureRequests,
    setSending,
    isLoading: _, // TODO @chris needs a silent loading method to be used on <CardTable/>
  } = useContext(SignatureRequestsContext);

  let signatureRequests = signatureRequestsFromContext;

  if (signatureRequestsAlreadyLoaded) {
    signatureRequests = signatureRequestsAlreadyLoaded;
  }

  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [sorting, setSorting] = useState<SortingState>([]);

  const [currentSignatureRequestId, setCurrentSignatureRequest] = useState<
    number | null
  >(null);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'company_name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'tax_year',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'document_name',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'status',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'updated_at',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'actions',
        cell: (info) => info.getValue(),
      },
    ],
    [],
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);

    addMeta({
      itemRank,
    });

    return itemRank.passed;
  };

  const table = useTable({
    columns,
    data: signatureRequests,
    globalFilterFn: fuzzyFilter,
    // debugTable: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
      globalFilter: search,
    },
    autoResetPageIndex: false,
  });

  const _getSignatureRequests = useCallback(async () => {
    if (refreshCompanySignatureRequests) {
      await refreshCompanySignatureRequests();
    } else {
      await getSignatureRequests();
    }
  }, [getSignatureRequests, refreshCompanySignatureRequests]);

  const currentSignatureRequest: SignatureRequest | undefined = useMemo(() => {
    return signatureRequests?.find(
      (SignatureRequest) => SignatureRequest.id === currentSignatureRequestId,
    );
  }, [signatureRequests, currentSignatureRequestId]);

  const _updateSignatureRequest = useCallback(
    async (signatureRequestId: number, companyId: number, data: any) => {
      await updateSignatureRequest({
        id: signatureRequestId,
        companyId,
        token,
        ...data,
      });
      await _getSignatureRequests();
    },
    [token, _getSignatureRequests],
  );

  const _deleteSignatureRequest = useCallback(
    async (signatureRequestId: number, companyId: number) => {
      await deleteSignatureRequest({
        id: signatureRequestId,
        companyId,
        token,
      });
      await _getSignatureRequests();
    },
    [token, _getSignatureRequests],
  );

  const showSignatureRequest = useCallback(
    (signatureRequestId: number) => {
      const signatureRequest: SignatureRequest | undefined =
        signatureRequests?.find(
          (signatureRequest) => signatureRequest.id === signatureRequestId,
        );

      if (!signatureRequest) return;
      if (signatureRequest.status === 'processing') {
        addToast(
          'info',
          'The signature request is still processing. Please wait for a few seconds.',
        );
      } else {
        setCurrentSignatureRequest(signatureRequestId);
      }
    },
    [addToast, signatureRequests],
  );

  if (!signatureRequests?.length) {
    return (
      <CardTable isInline={isInline} isLoading={false}>
        <EmptyState description="No signature request found" icon="signature">
          <ButtonGroup align="center">
            <Button
              intent="primary"
              iconRight="arrow-bend-up-right"
              onClick={() => setSending(true)}
              value="Send"
            />
          </ButtonGroup>
        </EmptyState>
      </CardTable>
    );
  }

  return (
    <>
      {currentSignatureRequest && (
        <SignatureRequestDialog
          signatureRequestId={currentSignatureRequest.id}
          companyId={currentSignatureRequest.company_id}
          onClose={() => setCurrentSignatureRequest(null)}
        />
      )}
      <CardTable isInline={isInline} isLoading={false}>
        <table className="sorabanTable">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn: boolean =
                    index === headerGroup.headers.length - 1;
                  const isSortingEnabled: boolean =
                    SORTING_ENABLED_COLUMNS.includes(
                      header.column.id as SignatureRequestColumnType,
                    );
                  const thClasses: string = getClasses({
                    sorabanTable__fixedColumn: isLastColumn,
                    extraPadding: isInline,
                  });

                  return (
                    <th
                      key={header.id}
                      data-sorted={header.column.getIsSorted()}
                      colSpan={header.colSpan}
                      className={thClasses}
                      style={{
                        width:
                          header.id === 'select' ? '32px' : header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <Flex
                          isYCentered={true}
                          gap={2}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <Heading level={6} isSameLine={true}>
                            {
                              SIGNATURE_REQUEST_TABLE_HEADER_MAP[
                                header.column.id as SignatureRequestColumnType
                              ]
                            }
                          </Heading>
                          {isSortingEnabled && (
                            <ButtonGroup>
                              {{
                                asc: (
                                  <Button
                                    value="x"
                                    intent="ghost"
                                    size="tiny"
                                    isIconOnly={true}
                                    iconLeft="arrow-up"
                                  />
                                ),
                                desc: (
                                  <Button
                                    value="x"
                                    intent="ghost"
                                    size="tiny"
                                    isIconOnly={true}
                                    iconLeft="arrow-down"
                                  />
                                ),
                              }[header.column.getIsSorted() as string] ?? (
                                <Button
                                  value="minus"
                                  intent="ghost"
                                  size="tiny"
                                  isIconOnly={true}
                                  iconLeft="minus"
                                />
                              )}
                            </ButtonGroup>
                          )}
                        </Flex>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const isLastColumn: boolean =
                      cellIndex === row.getVisibleCells().length - 1;

                    return (
                      <SignatureRequestCell
                        key={cell.id}
                        isLastColumn={isLastColumn}
                        type={cell.column.id as SignatureRequestColumnType}
                        signatureRequest={cell.row.original}
                        showSignatureRequest={showSignatureRequest}
                        updateSignatureRequest={_updateSignatureRequest}
                        deleteSignatureRequest={_deleteSignatureRequest}
                        hasExtraPadding={isInline}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardTable>
      <CardPagination>
        <ButtonGroup align="center">
          <Button
            intent="ghost"
            size="tiny"
            value="<<"
            isIconOnly={true}
            iconLeft="caret-double-left"
            onClick={() => table.firstPage()}
            isDisabled={!table.getCanPreviousPage()}
          />
          <Button
            intent="ghost"
            size="tiny"
            value="<"
            isIconOnly={true}
            iconLeft="caret-left"
            onClick={() => table.previousPage()}
            isDisabled={!table.getCanPreviousPage()}
          />
          <Select
            selectedValue={table.getState().pagination.pageSize.toString()}
            label="Page Size"
            isLabelHidden={true}
            items={PAGINATION_OPTIONS}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          />
          <Button
            intent="ghost"
            size="tiny"
            value=">"
            isIconOnly={true}
            iconRight="caret-right"
            onClick={() => table.nextPage()}
            isDisabled={!table.getCanNextPage()}
          />
          <Button
            intent="ghost"
            size="tiny"
            isIconOnly={true}
            value=">>"
            iconLeft="caret-double-right"
            onClick={() => table.lastPage()}
            isDisabled={!table.getCanNextPage()}
          />
        </ButtonGroup>
      </CardPagination>
    </>
  );
};
